import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Typography,
  Select,
  Button,
  Space,
  message,
  Row,
  Col,
  Input,
  List,
  Empty,
  Spin,
  Drawer,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  FlagOutlined,
} from "@ant-design/icons";
import { api } from "../../services/backend/api";
import RosterOrchestrator from "./RosterOrchestrator";
import { useParams } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;

const Roster = () => {
  const [selectedProjectId, setSelectedProjectId] = useState();
  const [selectedProjectObjectiveId, setSelectedProjectObjectiveId] =
    useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [projectSearchResults, setProjectSearchResults] = useState([]);
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [projectSearchOpen, setProjectSearchOpen] = useState(false);
  const projectDebounceRef = useRef(null);
  const params = useParams();

  const [poSearchQuery, setPOSearchQuery] = useState("");
  const [poSearchResults, setPOSearchResults] = useState([]);
  const [poSearchLoading, setPOSearchLoading] = useState(false);
  const [poSearchOpen, setPOSearchOpen] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const rosterOrchestratorRef = useRef(null);

  useEffect(() => {
    console.log("params", params);

    const projectId = params?.keys?.projectId;
    if (projectId) setSelectedProjectId(projectId);
  }, []);

  // Search projects with debounce
  useEffect(() => {
    if (projectDebounceRef.current) {
      clearTimeout(projectDebounceRef.current);
    }

    const term = (projectSearchQuery || "").trim();
    if (term.length < 3) {
      setProjectSearchResults([]);
      setProjectSearchLoading(false);
      return;
    }

    setProjectSearchLoading(true);
    projectDebounceRef.current = setTimeout(async () => {
      try {
        const data = await api.get(
          `/project/connected/search/${encodeURIComponent(term)}`,
        );
        setProjectSearchResults(Array.isArray(data) ? data : []);
        setProjectSearchOpen(true);
      } catch (error) {
        console.error("Error searching projects:", error);
        setProjectSearchResults([]);
      } finally {
        setProjectSearchLoading(false);
      }
    }, 400);

    return () => {
      if (projectDebounceRef.current) clearTimeout(projectDebounceRef.current);
    };
  }, [projectSearchQuery]);

  // Fetch project objectives when project is selected (for search dropdown)
  useEffect(() => {
    if (!selectedProjectId) {
      setPOSearchResults([]);
      setSelectedProjectObjectiveId(null);
      return;
    }

    const fetchObjectives = async () => {
      setPOSearchLoading(true);
      try {
        const data = await api.get(
          `/project/connected/${selectedProjectId}/objectives`,
        );
        setPOSearchResults(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching project objectives:", error);
        message.error("Failed to fetch project objectives");
        setPOSearchResults([]);
      } finally {
        setPOSearchLoading(false);
      }
    };

    fetchObjectives();
  }, [selectedProjectId]);

  // Get selected project name
  const selectedProjectName = useMemo(() => {
    if (!selectedProjectId) return null;
    const project = projectSearchResults.find(
      (p) => p.id === selectedProjectId,
    );
    return project?.name || selectedProjectId;
  }, [selectedProjectId, projectSearchResults]);

  // Get selected project objective name
  const selectedPOName = useMemo(() => {
    if (!selectedProjectObjectiveId) return null;
    const po = poSearchResults.find((p) => p.id === selectedProjectObjectiveId);
    return po?.name || selectedProjectObjectiveId;
  }, [selectedProjectObjectiveId, poSearchResults]);

  const handleClearFilters = () => {
    setSelectedProjectId(null);
    setSelectedProjectObjectiveId(null);
    setSelectedStatus(null);
    setProjectSearchQuery("");
    setPOSearchQuery("");
  };

  const handlePOSelect = (po) => {
    setSelectedProjectObjectiveId(po?.project_objective_id ?? null);
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Card
        style={{
          borderRadius: "12px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="mb-6">
          <Title level={2} style={{ marginBottom: 24 }}>
            Project Roster
          </Title>

          {/* Filters Section */}
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <div>
                  <Text strong>Project</Text>
                  <div className="relative mt-1">
                    <Input
                      placeholder="Search projects..."
                      value={selectedProjectName || projectSearchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setProjectSearchQuery(value);
                        if (!value) {
                          setSelectedProjectId(null);
                          setSelectedProjectObjectiveId(null);
                        }
                      }}
                      onFocus={() => {
                        if (projectSearchQuery.length >= 3) {
                          setProjectSearchOpen(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click on dropdown items
                        setTimeout(() => setProjectSearchOpen(false), 200);
                      }}
                      suffix={<SearchOutlined />}
                    />
                    {projectSearchOpen && projectSearchQuery.length >= 3 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                        {projectSearchLoading ? (
                          <div className="p-3 text-sm text-gray-500">
                            <Spin size="small" /> Searching...
                          </div>
                        ) : projectSearchResults.length === 0 ? (
                          <div className="p-3">
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="No projects found"
                            />
                          </div>
                        ) : (
                          <List
                            dataSource={projectSearchResults}
                            renderItem={(item) => (
                              <List.Item
                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setSelectedProjectId(item.id);
                                  setProjectSearchQuery("");
                                  setProjectSearchOpen(false);
                                  setSelectedProjectObjectiveId(null);
                                }}
                              >
                                <div className="w-full">
                                  <div className="text-gray-900 text-sm font-medium">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {item.id}
                                  </div>
                                </div>
                              </List.Item>
                            )}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <div>
                  <Text strong>Project Objective</Text>
                  <div className="relative mt-1">
                    <Input
                      placeholder="Search project objectives..."
                      value={selectedPOName || poSearchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPOSearchQuery(value);
                        if (!value) {
                          setSelectedProjectObjectiveId(null);
                        }
                      }}
                      onFocus={() => {
                        if (poSearchQuery.length >= 3 || selectedProjectId) {
                          setPOSearchOpen(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setPOSearchOpen(false), 200);
                      }}
                      disabled={!selectedProjectId}
                      suffix={<SearchOutlined />}
                    />
                    {poSearchOpen &&
                      selectedProjectId &&
                      (poSearchQuery.length >= 3 ||
                        poSearchResults.length > 0) && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                          {poSearchLoading ? (
                            <div className="p-3 text-sm text-gray-500">
                              <Spin size="small" /> Loading...
                            </div>
                          ) : poSearchResults.length === 0 ? (
                            <div className="p-3">
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No project objectives found"
                              />
                            </div>
                          ) : (
                            <List
                              dataSource={poSearchResults.filter((po) =>
                                poSearchQuery.length < 3
                                  ? true
                                  : po.name
                                      ?.toLowerCase()
                                      .includes(poSearchQuery.toLowerCase()),
                              )}
                              renderItem={(item) => (
                                <List.Item
                                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setSelectedProjectObjectiveId(item.id);
                                    setPOSearchQuery("");
                                    setPOSearchOpen(false);
                                  }}
                                >
                                  <div className="w-full">
                                    <div className="text-gray-900 text-sm font-medium">
                                      {item.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {item.id}
                                    </div>
                                  </div>
                                </List.Item>
                              )}
                            />
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={12} md={4}>
                <div>
                  <Text strong>Status</Text>
                  <Select
                    placeholder="All Statuses"
                    style={{ width: "100%", marginTop: 4 }}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                  >
                    <Option value="Active">Active</Option>
                    <Option value="Production">Production</Option>
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12} md={4}>
                <div style={{ marginTop: 24 }}>
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => rosterOrchestratorRef.current?.refresh?.()}
                      disabled={
                        !selectedProjectId && !selectedProjectObjectiveId
                      }
                    >
                      Refresh
                    </Button>
                    {(selectedProjectId || selectedProjectObjectiveId) &&
                      (lastSyncedAt ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Last refreshed at{" "}
                          {(() => {
                            try {
                              return new Date(lastSyncedAt).toLocaleString();
                            } catch {
                              return lastSyncedAt;
                            }
                          })()}
                        </Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          No data in cache. Click Refresh to load.
                        </Text>
                      ))}
                  </Space>
                </div>
              </Col>
            </Row>

            {(selectedProjectId || selectedProjectObjectiveId) && (
              <Button
                type="link"
                onClick={handleClearFilters}
                style={{ padding: 0 }}
              >
                Clear Filters
              </Button>
            )}
          </Space>
        </div>

        {/* Roster: orchestrator loads data via rosterService and renders view */}
        {selectedProjectId || selectedProjectObjectiveId ? (
          <RosterOrchestrator
            ref={rosterOrchestratorRef}
            key={`${selectedProjectId}-${selectedProjectObjectiveId}`}
            projectId={selectedProjectId}
            objectiveId={selectedProjectObjectiveId}
            onPOSelect={handlePOSelect}
            onLastSyncedAtChange={setLastSyncedAt}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <Empty
              description="Select a project or project objective to view roster"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </Card>
      <Drawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Roster"
      >
        {drawerContent?.data?.selectedProjectObjectiveId && (
          <p>
            PO selected: {drawerContent.data.selectedProjectObjectiveId}. Use
            the table above to view roster for a project objective.
          </p>
        )}
      </Drawer>
    </div>
  );
};

export default Roster;
